import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTick } from '@pixi/react';
import type { Graphics as PixiGraphicsClass } from 'pixi.js';
import type { GameState } from './GameComponent'; 
import type { BirdBounds } from './BirdComponent';

interface PipeData {
  id: number; 
  x: number;
  topPipeHeight: number;
  gapY: number; 
  scored: boolean;
}

interface PipeManagerComponentProps {
  gameState: GameState;
  screenWidth: number;
  screenHeight: number;
  birdBounds: BirdBounds | null; 
  onScorePoint: () => void; 
  onCollision: () => void;
  pipeSpeed?: number;
  pipeGapVertical?: number;
  spawnIntervalFrames?: number;
}

const DEFAULT_PIPE_WIDTH = 80; 
const DEFAULT_PIPE_COLOR = 0x2E7D32; 
const DEFAULT_PIPE_CAP_COLOR = 0x4CAF50; 
const DEFAULT_PIPE_CAP_HIGHLIGHT_COLOR = 0x66BB6A; 
const DEFAULT_PIPE_OUTLINE_COLOR = 0x1B5E20; 
const DEFAULT_PIPE_OUTLINE_WIDTH = 2;
const DEFAULT_PIPE_GAP_VERTICAL = 190; 
const DEFAULT_MIN_PIPE_HEIGHT = 60; 
const DEFAULT_PIPE_SPEED = 2.8; 
const DEFAULT_SPAWN_INTERVAL_FRAMES = 110; 
const DEFAULT_PIPE_CAP_HEIGHT = 30;    
const DEFAULT_PIPE_CAP_OVERHANG = 5;   
const COLLISION_BUFFER = 12; 

let nextPipeId = 0; 

const PipeManagerComponent: React.FC<PipeManagerComponentProps> = ({
  gameState,
  screenWidth,
  screenHeight,
  birdBounds,
  onScorePoint,
  onCollision,
  pipeSpeed: propPipeSpeed,
  pipeGapVertical: propPipeGapVertical,
  spawnIntervalFrames: propSpawnIntervalFrames,
}) => {
  const currentPipeSpeed = propPipeSpeed !== undefined ? propPipeSpeed : DEFAULT_PIPE_SPEED;
  const currentPipeGapVertical = propPipeGapVertical !== undefined ? propPipeGapVertical : DEFAULT_PIPE_GAP_VERTICAL;
  const currentSpawnIntervalFrames = propSpawnIntervalFrames !== undefined ? propSpawnIntervalFrames : DEFAULT_SPAWN_INTERVAL_FRAMES;

  const [pipePairs, setPipePairs] = useState<PipeData[]>([]);
  const spawnTimer = useRef<number>(currentSpawnIntervalFrames); 

  const spawnPipePair = useCallback(() => {
    const availableSpaceForGap = screenHeight - (2 * DEFAULT_MIN_PIPE_HEIGHT) - currentPipeGapVertical;
    const gapCenterY = DEFAULT_MIN_PIPE_HEIGHT + currentPipeGapVertical / 2 + Math.random() * availableSpaceForGap;
    const topPipeHeight = gapCenterY - currentPipeGapVertical / 2;

    setPipePairs(prevPipes => [
      ...prevPipes,
      {
        id: nextPipeId++,
        x: screenWidth,
        topPipeHeight: topPipeHeight,
        gapY: gapCenterY,
        scored: false,
      },
    ]);
  }, [screenWidth, screenHeight, currentPipeGapVertical]);

  useEffect(() => {
    if (gameState === 'playing') {
      setPipePairs([]);
      spawnTimer.current = currentSpawnIntervalFrames; 
      nextPipeId = 0;
      console.log('PipeManager: Pipes reset for new "playing" state.');
    } else if (gameState === 'menu') {
      setPipePairs([]);
      spawnTimer.current = currentSpawnIntervalFrames; 
      nextPipeId = 0;
      console.log('PipeManager: Pipes reset for "menu" state.');
    }
  }, [gameState, currentSpawnIntervalFrames]);

  useTick((ticker) => {
    if (gameState !== 'playing') return;

    const delta = ticker.deltaTime;

    spawnTimer.current -= delta;
    if (spawnTimer.current <= 0) {
      spawnPipePair();
      spawnTimer.current = currentSpawnIntervalFrames;
    }

    let collisionHasOccurred = false;
    setPipePairs(prevPipes =>
      prevPipes
        .map(pipe => {
          const newX = pipe.x - currentPipeSpeed * delta;
          if (birdBounds && !collisionHasOccurred && !pipe.scored) { // pipe.scored check here is for collision, not scoring point
            const pipeHitboxXStart = newX + COLLISION_BUFFER;
            const pipeHitboxWidth = DEFAULT_PIPE_WIDTH - COLLISION_BUFFER;
            const effectivePipeHitboxWidth = Math.max(0, pipeHitboxWidth);

            const topPipeRect = { x: pipeHitboxXStart, y: 0, width: effectivePipeHitboxWidth, height: pipe.topPipeHeight };
            const bottomPipeY = pipe.gapY + currentPipeGapVertical / 2;
            const bottomPipeRect = {
              x: pipeHitboxXStart,
              y: bottomPipeY,
              width: effectivePipeHitboxWidth, 
              height: screenHeight - bottomPipeY
            };
            
            if (intersects(birdBounds, topPipeRect) || intersects(birdBounds, bottomPipeRect)) {
              onCollision();
              collisionHasOccurred = true;
            }
          }
          return { ...pipe, x: newX };
        })
        .filter(pipe => pipe.x + DEFAULT_PIPE_WIDTH + (DEFAULT_PIPE_CAP_OVERHANG * 2) > 0)
    );
  });

  useEffect(() => {
    // DIAGNOSTIC LOG 1: Check if effect runs and basic conditions
    console.log(`[PipeManager Scoring Effect] gameState: ${gameState}, birdBounds: ${birdBounds ? `x: ${birdBounds.x.toFixed(2)}, y: ${birdBounds.y.toFixed(2)}` : 'null'}`);

    if (gameState === 'playing' && birdBounds) {
      setPipePairs(prevPipes =>
        prevPipes.map(pipe => {
          // DIAGNOSTIC LOG 2: Log values for each pipe before checking score condition
          console.log(`[PipeManager Scoring Check] Pipe ID: ${pipe.id}, Pipe X: ${pipe.x.toFixed(2)}, Pipe Scored: ${pipe.scored}, Bird X: ${birdBounds.x.toFixed(2)}, Pipe Width: ${DEFAULT_PIPE_WIDTH}`);
          
          const scoreConditionMet = !pipe.scored && (pipe.x + DEFAULT_PIPE_WIDTH) < birdBounds.x;
          // DIAGNOSTIC LOG 3: Log the scoring condition result
          console.log(`[PipeManager Scoring Condition] For Pipe ID ${pipe.id}: ${scoreConditionMet} (Condition: !${pipe.scored} && (${pipe.x.toFixed(2)} + ${DEFAULT_PIPE_WIDTH}) < ${birdBounds.x.toFixed(2)})`);

          if (scoreConditionMet) {
            // DIAGNOSTIC LOG 4: Log when onScorePoint is called
            console.log(`[PipeManager SCORE POINT!] Pipe ID: ${pipe.id}. Calling onScorePoint.`);
            onScorePoint();
            return { ...pipe, scored: true };
          }
          return pipe;
        })
      );
    }
  }, [birdBounds, gameState, onScorePoint]);

  const drawPipe = useCallback((g: PixiGraphicsClass, currentPipeWidthArg: number, currentPipeHeight: number, isTopPipe: boolean) => {
    g.clear();
    
    const bodyWidth = currentPipeWidthArg;
    const capActualWidth = bodyWidth + DEFAULT_PIPE_CAP_OVERHANG * 2;
    const capX = -DEFAULT_PIPE_CAP_OVERHANG;
    const lineStyleOptions = { width: DEFAULT_PIPE_OUTLINE_WIDTH, color: DEFAULT_PIPE_OUTLINE_COLOR, alpha: 1 };

    if (currentPipeHeight <= DEFAULT_PIPE_CAP_HEIGHT) {
      // PixiJS v8: Use new Graphics API
      g.stroke(lineStyleOptions)
       .rect(0, 0, bodyWidth, currentPipeHeight)
       .fill(DEFAULT_PIPE_COLOR);
      return;
    }

    const bodyHeight = currentPipeHeight - DEFAULT_PIPE_CAP_HEIGHT;

    if (isTopPipe) {
      g.stroke(lineStyleOptions)
       .rect(0, 0, bodyWidth, bodyHeight)
       .fill(DEFAULT_PIPE_COLOR);
      
      g.stroke(lineStyleOptions)
       .rect(capX, bodyHeight, capActualWidth, DEFAULT_PIPE_CAP_HEIGHT)
       .fill(DEFAULT_PIPE_CAP_COLOR);
      
      g.rect(capX + DEFAULT_PIPE_OUTLINE_WIDTH, bodyHeight + DEFAULT_PIPE_OUTLINE_WIDTH, capActualWidth - (DEFAULT_PIPE_OUTLINE_WIDTH*2), DEFAULT_PIPE_CAP_HEIGHT * 0.25)
       .fill({color: DEFAULT_PIPE_CAP_HIGHLIGHT_COLOR, alpha: 0.5 });

    } else {
      g.stroke(lineStyleOptions)
       .rect(0, DEFAULT_PIPE_CAP_HEIGHT, bodyWidth, bodyHeight)
       .fill(DEFAULT_PIPE_COLOR);
      
      g.stroke(lineStyleOptions)
       .rect(capX, 0, capActualWidth, DEFAULT_PIPE_CAP_HEIGHT)
       .fill(DEFAULT_PIPE_CAP_COLOR);
      
      g.rect(capX + DEFAULT_PIPE_OUTLINE_WIDTH, DEFAULT_PIPE_CAP_HEIGHT - (DEFAULT_PIPE_CAP_HEIGHT*0.25) - DEFAULT_PIPE_OUTLINE_WIDTH, capActualWidth - (DEFAULT_PIPE_OUTLINE_WIDTH*2), DEFAULT_PIPE_CAP_HEIGHT * 0.25)
       .fill({color: DEFAULT_PIPE_CAP_HIGHLIGHT_COLOR, alpha: 0.5 });
    }
  }, []); // Removed screenWidth, screenHeight as they are not directly used in drawPipe logic itself, only passed to it

  return (
    <>
      {pipePairs.map(pipe => (
        <React.Fragment key={pipe.id}>
          {/* Top Pipe */}
          <pixiGraphics
            x={pipe.x}
            y={0}
            draw={(g) => drawPipe(g, DEFAULT_PIPE_WIDTH, pipe.topPipeHeight, true)}
            zIndex={5}
          />
          {/* Bottom Pipe */}
          <pixiGraphics
            x={pipe.x}
            y={pipe.gapY + currentPipeGapVertical / 2}
            draw={(g) => drawPipe(g, DEFAULT_PIPE_WIDTH, screenHeight - (pipe.gapY + currentPipeGapVertical / 2), false)}
            zIndex={5}
          />
        </React.Fragment>
      ))}
    </>
  );
};

function intersects(
    rectA: { x: number; y: number; width: number; height: number }, 
    rectB: { x: number; y: number; width: number; height: number }
): boolean {
  return rectA.x < rectB.x + rectB.width &&
         rectA.x + rectA.width > rectB.x &&
         rectA.y < rectB.y + rectB.height &&
         rectA.y + rectA.height > rectB.y;
}

export default PipeManagerComponent;
// Rewrite Count: 14
